import { firebaseAuth } from 'config/firebase';
import { RecaptchaVerifier } from 'firebase/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logError } from 'utils/logError';

export const useRecaptchaVerifier = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const [recaptchaVerifier, setRecaptchaVerifier] =
    useState<RecaptchaVerifier>();

  const refocusPreviousElement = () => {
    if (
      previousFocusRef.current &&
      typeof previousFocusRef.current.focus === 'function'
    ) {
      previousFocusRef.current.focus();
    }
  };

  const createVerifier = useCallback(() => {
    let verifier: RecaptchaVerifier | undefined;

    if (!recaptchaVerifier && containerRef.current !== null) {
      // For reference: https://developers.google.com/recaptcha/docs/invisible#render_param
      verifier = new RecaptchaVerifier(firebaseAuth, containerRef.current, {
        size: 'invisible',
        badge: 'inline',
        // Refocus previously focused element after verification
        callback: refocusPreviousElement,
      });
    }

    return verifier;
  }, [recaptchaVerifier]);

  const clearVerifier = useCallback(
    (verifier: RecaptchaVerifier | undefined) => {
      setRecaptchaVerifier(undefined);
      verifier?.clear();
      if (containerRef.current !== null) {
        containerRef.current.innerHTML = '';
      }
    },
    [],
  );

  useEffect(() => {
    // The recaptchaVerifier must be created after the containerRef element is mounted
    const observer = new MutationObserver((_mutations, obs) => {
      if (containerRef.current && document.contains(containerRef.current)) {
        if (!recaptchaVerifier) {
          // Save the currently focused element before verification
          previousFocusRef.current = document.activeElement as HTMLElement;

          const newVerifier = createVerifier();
          newVerifier
            ?.render()
            .then(() => {
              setRecaptchaVerifier(newVerifier);
            })
            .catch(logError);
        }
        obs.disconnect(); // Stop observing once we've found the element
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearVerifier(recaptchaVerifier);
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { recaptchaVerifier, containerRef };
};
